import { GridLegacy as Grid } from '@mui/material'

import { C1Button } from 'c1ui'
import { C1CheckboxPrettyDisplay as Checkbox } from 'components/core/checkbox'

import { PluralFacetFilter } from 'components/core/search-filter/facet'
import { LiveQueryFilter } from 'components/core/search-filter/query'
import { MetaFields } from 'lib/search'
import {
    AppEntitlementSearchServiceSearchRequest,
    AppEntitlementSearchServiceSearchTermsRequest,
    AppEntitlementSearchServiceSearchTermsRequest_TermType as TermType,
} from 'pbts/c1/api/app/v1/app_entitlement'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { appEntitlementSearchTermsAPI } from 'redux_toolkit/entitlements'

type FilterKeys = keyof Omit<AppEntitlementSearchServiceSearchRequest, MetaFields>
type FilterUpdate = Partial<Pick<AppEntitlementSearchServiceSearchRequest, FilterKeys>>
export type AppEntitlementFilters = 'query' | 'app' | 'resourceType' | 'riskLevel' | 'complianceFramework' | 'includeDeleted'
export type FilterOnChange = (updated: FilterUpdate) => void
type Props = {
    initialState: AppEntitlementSearchServiceSearchRequest
    onChange: (filter: AppEntitlementSearchServiceSearchRequest) => void
    hiddenFilters?: AppEntitlementFilters[]
}

export const AppEntitlementSearchFilter: React.FC<Props> = ({
    onChange: _onChange,
    initialState,
    hiddenFilters = [],
}) => {
    const initialFilter = useRef(initialState).current
    const [filter, setFilter] = useState(() =>
        AppEntitlementSearchServiceSearchRequest.create(initialState ? { ...initialState } : {}))

    const onChange: FilterOnChange = useCallback(
        (update): void => {
            let newFilter
            let updated = false
            setFilter((old) => {
                newFilter = AppEntitlementSearchServiceSearchRequest.clone(old)
                AppEntitlementSearchServiceSearchRequest.mergePartial(newFilter, update)
                if (AppEntitlementSearchServiceSearchRequest.equals(newFilter, old)) {
                    return old
                }
                updated = true
                return newFilter
            })
            // react won't call setFilters recursively it seems...
            if (!newFilter || !updated) {
                return
            }
            _onChange(newFilter)
        },
        [setFilter, _onChange],
    )
    const onQueryChange = useCallback((query: string): void => onChange({ query }), [onChange])
    const clear = useCallback((): void => {
        return onChange(initialFilter)
    }, [initialFilter, onChange])

    const canClear = useMemo(
        (): boolean => !AppEntitlementSearchServiceSearchRequest.equals(filter, initialFilter),
        [filter, initialFilter],
    )

    // NOTE: the hook we use is *more general* than the request we use to populate the list
    // AppEntitlementSearchServiceSearchRequest -> AppEntitlementSearchServiceSearchRequest
    const correctedType = useMemo(() => AppEntitlementSearchServiceSearchRequest.create(filter), [filter])

    const intl = useIntl()
    return <>
        {!hiddenFilters.includes('query') && <Grid item>
            <LiveQueryFilter onChange={onQueryChange} query={filter.query} />
        </Grid>}
        {!hiddenFilters.includes('app') && (
            <Grid item>
                <PluralFacetFilter<AppEntitlementSearchServiceSearchTermsRequest, 'appIds'>
                    widePopper
                    onChange={onChange}
                    filter={correctedType}
                    term={TermType.APPS}
                    filterKey="appIds"
                    label={intl.formatMessage({ defaultMessage: 'Apps', id: 'apps' })}
                    hook={appEntitlementSearchTermsAPI.useLazyGetSearchTerms}
                />
            </Grid>
        )}
        {!hiddenFilters.includes('resourceType') && <Grid item>
            <PluralFacetFilter<AppEntitlementSearchServiceSearchTermsRequest, 'resourceTypeIds'>
                onChange={onChange}
                filter={correctedType}
                term={TermType.RESOURCE_TYPE}
                filterKey="resourceTypeIds"
                label={intl.formatMessage({ defaultMessage: 'Resource type', id: 'Resource type' })}
                hook={appEntitlementSearchTermsAPI.useLazyGetSearchTerms}
            />
        </Grid>}
        {!hiddenFilters.includes('riskLevel') && <Grid item>
            <PluralFacetFilter<AppEntitlementSearchServiceSearchTermsRequest, 'riskLevelIds'>
                onChange={onChange}
                filter={correctedType}
                term={TermType.RISK_LEVEL}
                filterKey="riskLevelIds"
                label={intl.formatMessage({ defaultMessage: 'Risk level', id: 'Risk level' })}
                hook={appEntitlementSearchTermsAPI.useLazyGetSearchTerms}
            />
        </Grid>}
        {!hiddenFilters.includes('complianceFramework') && <Grid item>
            <PluralFacetFilter<AppEntitlementSearchServiceSearchTermsRequest, 'complianceFrameworkIds'>
                onChange={onChange}
                filter={correctedType}
                term={TermType.COMPLIANCE_FRAMEWORK}
                filterKey="complianceFrameworkIds"
                label={intl.formatMessage({ defaultMessage: 'Compliance framework', id: 'Compliance framework' })}
                hook={appEntitlementSearchTermsAPI.useLazyGetSearchTerms}
            />
        </Grid>}
        {!hiddenFilters.includes('includeDeleted') && <Grid item>
            <Checkbox
                checked={!!filter.includeDeleted}
                id="include-deleted-entitlements"
                label={intl.formatMessage({
                    id: 'include-deleted-entitlements',
                    defaultMessage: 'Include deleted entitlements',
                })}
                onChange={(_event, checked): void => {
                    onChange({ includeDeleted: checked })
                }}
            />
        </Grid>}

        {canClear && (
            <Grid key="clear" item>
                <C1Button onClick={clear} variant="text" color="info">
                    <FormattedMessage id="components_entitlement_search_index_clear" defaultMessage="Clear" />
                </C1Button>
            </Grid>
        )}
    </>
}
